import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Container,
  Form,
  FormGroup,
  Label,
  Input,
  Spinner,
  Row,
  Col,
  Alert
} from "reactstrap";
import axios from "axios";
import { API_BASE_URL } from "../../constants";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import { Toast } from "primereact/toast";
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';

const BlogAdd = () => {
  const [searchParams] = useSearchParams();
  const editId = searchParams.get('edit');
  const navigate = useNavigate();
  const toast = useRef(null);

  const [loading, setLoading] = useState(false);
  const [loadingBlog, setLoadingBlog] = useState(false);
  const [fetchError, setFetchError] = useState(null);

  const [formData, setFormData] = useState({
    office: "coders",
    type: "blog",
    title: "",
    description: "",
    content: "",
    image: null,
    status: "draft",
    meta_title: "",
    meta_description: "",
    meta_keywords: "",
    tags: ""
  });
  const [imagePreview, setImagePreview] = useState(null);

  const breadcrumbItems = useMemo(() => [
    { title: "Content", link: "/" },
    { title: "Blogs", link: "/admin/office/website/blogs" },
    { title: editId ? "Edit Blog" : "Add Blog", link: "#!" },
  ], [editId]);

  const officeOptions = [
    { value: 'coders', label: 'Coders' },
    { value: 'creatives', label: 'Creatives' }
  ];

  const typeOptions = [
    { value: 'blog', label: 'Blog' },
    { value: 'case_study', label: 'Case Study' }
  ];

  const statusOptions = [
    { value: 'draft', label: 'Draft' },
    { value: 'published', label: 'Published' }
  ];

  const fetchBlogData = useCallback(async () => {
    setLoadingBlog(true);
    setFetchError(null);
    try {
      const response = await axios.get(`${API_BASE_URL}/blog/${editId}`, {
        headers: { Authorization: "Bearer " + localStorage.getItem("token") }
      });

      const blogData = response.data.data;

      if (blogData) {
        setFormData({
          office: blogData.office || "coders",
          type: blogData.type || "blog",
          title: blogData.title || "",
          description: blogData.description || "",
          content: blogData.content || "",
          status: blogData.status || "draft",
          image: null,
          meta_title: blogData.meta_title || "",
          meta_description: blogData.meta_description || "",
          meta_keywords: blogData.meta_keywords || "",
          tags: blogData.tags || ""
        });

        if (blogData.image_url) {
          setImagePreview(blogData.image_url);
        }
      }
    } catch (error) {
      setFetchError("Failed to load blog data");
    } finally {
      setLoadingBlog(false);
    }
  }, [editId]);

  useEffect(() => {
    if (editId) fetchBlogData();
  }, [editId, fetchBlogData]);

  // CKEditor Upload Adapter (Same as Portfolio)
  class MyUploadAdapter {
    constructor(loader) { this.loader = loader; }
    upload() {
      return this.loader.file.then(file => new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve({ default: reader.result });
        reader.onerror = error => reject(error);
        reader.readAsDataURL(file);
      }));
    }
    abort() {}
  }

  function MyCustomUploadAdapterPlugin(editor) {
    editor.plugins.get('FileRepository').createUploadAdapter = (loader) => new MyUploadAdapter(loader);
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData({ ...formData, image: file });
      const reader = new FileReader();
      reader.onloadend = () => setImagePreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const showToast = (severity, summary, detail) => {
    toast.current?.show({ severity, summary, detail, life: 3000 });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const formDataToSend = new FormData();
      Object.keys(formData).forEach(key => {
        if (key === 'image') {
          if (formData.image instanceof File) formDataToSend.append('image', formData.image);
        } else {
          formDataToSend.append(key, formData[key] || "");
        }
      });

      const url = editId
        ? `${API_BASE_URL}/blog/update/${editId}`
        : `${API_BASE_URL}/blog/add`;

      await axios.post(url, formDataToSend, {
        headers: {
          Authorization: "Bearer " + localStorage.getItem("token"),
          "Content-Type": "multipart/form-data",
        }
      });

      showToast('success', 'Success', editId ? 'Updated successfully' : 'Created successfully');
      setTimeout(() => navigate('/admin/office/website/blogs'), 1500);
    } catch (error) {
      const errorMsg = error.response?.data?.message || "Operation failed";
      showToast('error', 'Error', errorMsg);
    } finally {
      setLoading(false);
    }
  };

  if (loadingBlog) {
    return (
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Edit Blog" breadcrumbItems={breadcrumbItems} />
          <Card><CardBody className="text-center py-5"><Spinner color="primary" /></CardBody></Card>
        </Container>
      </div>
    );
  }

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title={editId ? "Edit Blog" : "Add Blog"} breadcrumbItems={breadcrumbItems} />
          <Card>
            <CardHeader>
              <h4 className="card-title mb-0">{editId ? 'Edit Blog' : 'Create New Blog'}</h4>
            </CardHeader>
            <CardBody>
              {fetchError && <Alert color="danger">{fetchError}</Alert>}

              <Form onSubmit={handleSubmit}>
                <Row>
                  <Col lg={8}>
                    <FormGroup>
                      <Label for="title">Title <span className="text-danger">*</span></Label>
                      <Input
                        type="text"
                        name="title"
                        value={formData.title}
                        onChange={handleInputChange}
                        placeholder="Enter blog title"
                        required
                      />
                    </FormGroup>

                    <Row>
                      <Col md={4}>
                        <FormGroup>
                          <Label for="office">Office</Label>
                          <Input type="select" name="office" value={formData.office} onChange={handleInputChange} className="form-select">
                            {officeOptions.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                          </Input>
                        </FormGroup>
                      </Col>
                      <Col md={4}>
                        <FormGroup>
                          <Label for="type">Type</Label>
                          <Input type="select" name="type" value={formData.type} onChange={handleInputChange} className="form-select">
                            {typeOptions.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                          </Input>
                        </FormGroup>
                      </Col>
                      <Col md={4}>
                        <FormGroup>
                          <Label for="status">Status</Label>
                          <Input type="select" name="status" value={formData.status} onChange={handleInputChange} className="form-select">
                            {statusOptions.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                          </Input>
                        </FormGroup>
                      </Col>
                    </Row>
                  </Col>

                  <Col lg={4}>
                    <Card className="shadow-sm">
                      <CardBody>
                        <FormGroup>
                          <Label for="image">Featured Image</Label>
                          <Input type="file" onChange={handleImageChange} accept="image/*" />
                          {imagePreview && (
                            <div className="mt-2 text-center">
                              <img src={imagePreview} alt="Preview" className="img-thumbnail" style={{ maxHeight: "150px" }} />
                            </div>
                          )}
                        </FormGroup>
                      </CardBody>
                    </Card>
                  </Col>
                </Row>

                <Row className="mt-3">
                  <Col lg={12}>
                    <FormGroup>
                      <Label for="content">Content <span className="text-danger">*</span></Label>
                      <div className="border rounded">
                        <CKEditor
                          editor={ClassicEditor}
                          data={formData.content}
                          config={{ extraPlugins: [MyCustomUploadAdapterPlugin] }}
                          onChange={(event, editor) => {
                            const data = editor.getData();
                            setFormData(prev => ({ ...prev, content: data }));
                          }}
                        />
                      </div>
                    </FormGroup>
                  </Col>
                </Row>

                <Row className="mt-3">
                  <Col lg={12}>
                    <Card className="shadow-sm">
                      <CardHeader><h5 className="mb-0">SEO Settings</h5></CardHeader>
                      <CardBody>
                        <Row>
                          <Col md={6}>
                            <FormGroup>
                              <Label for="meta_title">Meta Title</Label>
                              <Input name="meta_title" value={formData.meta_title} onChange={handleInputChange} />
                            </FormGroup>
                            <FormGroup>
                              <Label for="meta_keywords">Meta Keywords <span className="text-danger">*</span></Label>
                              <Input name="meta_keywords" value={formData.meta_keywords} onChange={handleInputChange} required />
                            </FormGroup>
                          </Col>
                          <Col md={6}>
                            <FormGroup>
                              <Label for="meta_description">Meta Description</Label>
                              <Input type="textarea" name="meta_description" value={formData.meta_description} onChange={handleInputChange} rows={5} />
                            </FormGroup>
                          </Col>
                        </Row>
                      </CardBody>
                    </Card>
                  </Col>
                </Row>

                <div className="mt-4 d-flex justify-content-between">
                  <Button type="button" color="light" onClick={() => navigate(-1)}>Cancel</Button>
                  <Button type="submit" color="primary" disabled={loading}>
                    {loading ? <Spinner size="sm" className="me-2" /> : null}
                    {editId ? 'Update Blog' : 'Save Blog'}
                  </Button>
                </div>
              </Form>
            </CardBody>
          </Card>
        </Container>
      </div>
      <Toast ref={toast} position="top-right" />
    </React.Fragment>
  );
};

export default BlogAdd;